import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, DateField, Input, Select, SelectItem } from '@structural-climbing/ui'
import { GymDialog } from '../../components/GymDialog'

export interface EditableMemberDetails {
  firstName: string | null
  lastName: string | null
  phone: string | null
  gender: string | null
  dateOfBirth: string | null
}

interface Props {
  userId: string
  details: EditableMemberDetails
  onSaved: () => void
  onClose: () => void
}

type Field = 'firstName' | 'lastName' | 'phone'

const GENDERS = ['male', 'female', 'prefer_not_to_say'] as const

/**
 * The gym correcting what it has on record about a member. Only what changed is
 * sent, so a field nobody touched cannot be refused, and a name left as it was
 * does not send the member back to sign their documents.
 */
export function EditMemberDetailsModal({ userId, details, onSaved, onClose }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const saved = {
    firstName: details.firstName ?? '',
    lastName: details.lastName ?? '',
    phone: details.phone ?? '',
    gender: details.gender ?? null,
    dateOfBirth: details.dateOfBirth ?? '',
  }

  const [firstName, setFirstName] = useState(saved.firstName)
  const [lastName, setLastName] = useState(saved.lastName)
  const [phone, setPhone] = useState(saved.phone)
  const [gender, setGender] = useState<string | null>(saved.gender)
  const [dateOfBirth, setDateOfBirth] = useState(saved.dateOfBirth)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const typedFirst = firstName.trim()
  const typedLast = lastName.trim()
  const typedPhone = phone.trim()
  const nameChanged = typedFirst !== saved.firstName || typedLast !== saved.lastName

  const genderLabels: Record<(typeof GENDERS)[number], string> = {
    male: t('members.genderMale'),
    female: t('members.genderFemale'),
    prefer_not_to_say: t('members.genderPreferNotToSay'),
  }

  async function handleSave() {
    const errors: Partial<Record<Field, string>> = {}
    const body: Record<string, unknown> = {}

    // A name or a phone number can be corrected, never blanked.
    if (typedFirst !== saved.firstName) {
      if (typedFirst) body.firstName = typedFirst
      else errors.firstName = t('members.editDetailsRequired')
    }
    if (typedLast !== saved.lastName) {
      if (typedLast) body.lastName = typedLast
      else errors.lastName = t('members.editDetailsRequired')
    }
    if (typedPhone !== saved.phone) {
      if (typedPhone) body.phone = typedPhone
      else errors.phone = t('members.editDetailsPhoneRequired')
    }
    if (dateOfBirth !== saved.dateOfBirth) body.dateOfBirth = dateOfBirth || null
    if (gender !== saved.gender) body.gender = gender

    setFieldErrors(errors)
    setError(null)
    if (Object.keys(errors).length > 0) return
    if (Object.keys(body).length === 0) {
      onClose()
      return
    }

    setSaving(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/members/${encodeURIComponent(userId)}/profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { code?: string }
        if (data.code === 'PHONE_INVALID')
          setFieldErrors({ phone: t('members.editDetailsPhoneInvalid') })
        else if (data.code === 'PHONE_REQUIRED')
          setFieldErrors({ phone: t('members.editDetailsPhoneRequired') })
        else if (data.code === 'CHILD_ACCOUNT') setError(t('members.editDetailsChildAccount'))
        else setError(t('common.errorDefault'))
        return
      }
      onSaved()
    } catch {
      setError(t('common.networkError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <GymDialog onClose={onClose} isDismissable={!saving}>
      <form
        className="overflow-y-auto p-6"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void handleSave()
        }}
      >
        <h2 className="mb-4 text-base font-semibold">{t('members.editDetails')}</h2>

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label={t('members.detailsFirstName')}
            value={firstName}
            onValueChange={setFirstName}
            maxLength={100}
            isInvalid={!!fieldErrors.firstName}
            errorMessage={fieldErrors.firstName}
          />
          <Input
            label={t('members.detailsLastName')}
            value={lastName}
            onValueChange={setLastName}
            maxLength={100}
            isInvalid={!!fieldErrors.lastName}
            errorMessage={fieldErrors.lastName}
          />
          <Input
            type="tel"
            label={t('members.detailsPhone')}
            value={phone}
            onValueChange={setPhone}
            maxLength={20}
            isInvalid={!!fieldErrors.phone}
            errorMessage={fieldErrors.phone}
          />
          <DateField
            label={t('members.detailsDateOfBirth')}
            value={dateOfBirth}
            onValueChange={setDateOfBirth}
          />
          <Select
            label={t('members.detailsGender')}
            selectedKeys={gender ? new Set([gender]) : new Set<string>()}
            onSelectionChange={(keys) => {
              const key = [...keys][0] as string | undefined
              setGender(key ?? null)
            }}
          >
            {GENDERS.map((key) => (
              <SelectItem key={key}>{genderLabels[key]}</SelectItem>
            ))}
          </Select>
        </div>

        {nameChanged && (
          <p
            role="status"
            className="mt-4 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning-700 dark:text-warning"
          >
            {t('members.editDetailsResignNotice')}
          </p>
        )}

        {error && <p className="mt-3 text-sm text-danger">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <Button size="sm" variant="flat" isDisabled={saving} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" color="primary" type="submit" isLoading={saving}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </GymDialog>
  )
}
